import { useState } from 'react';
import { useCrudPermissions } from '@/hooks/useCrudPermissions';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import { CrudTable } from '@/components/CrudTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormTextField } from '@/components/form/FormTextField';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getLeaveTypes,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  toggleLeaveTypeDisabled,
  type ILeaveType,
} from '@/api/leave.api';
import { safeArray } from '@/lib/safeArray';
import { getApiErrorMessage, buildErrorToast } from '@/lib/apiError';


const leaveTypeSchema = z.object({
  code: z.string().min(1, 'Code is required').max(50).regex(/^[A-Z_]+$/, 'Code must be UPPERCASE letters and underscores'),
  name: z.string().min(1, 'Name is required').max(200),
  colorHex: z.string().max(7).optional(),
  isPaid: z.boolean(),
  requiresAttachment: z.boolean(),
  note: z.string().optional(),
});

type LeaveTypeFormValues = z.infer<typeof leaveTypeSchema>;

const baseColumns: ColumnDef<ILeaveType>[] = [
  { accessorKey: 'orderNo', header: 'Order', size: 5, cell: ({ row }) => row.original.orderNo ?? 0 },
  { accessorKey: 'code', header: 'Code', size: 10, cell: ({ row }) => (
    <span className="break-all font-mono text-xs">{row.original.code}</span>
  )},
  {
    accessorKey: 'name',
    header: 'Name',
    size: 28,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.colorHex && (
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: row.original.colorHex }}
          />
        )}
        <span>{row.original.name}</span>
      </div>
    ),
  },
  {
    accessorKey: 'isPaid',
    header: 'Paid',
    size: 9,
    cell: ({ row }) => (
      <span className={`text-xs px-2 py-0.5 rounded-full ${row.original.isPaid ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
        {row.original.isPaid ? 'Yes' : 'No'}
      </span>
    ),
  },
  {
    accessorKey: 'requiresAttachment',
    header: 'Requires Attachment',
    size: 25,
    cell: ({ row }) => row.original.requiresAttachment ? '📎 Yes' : '—',
  },
];

export function LeaveTypesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ILeaveType | null>(null);
  const queryClient = useQueryClient();

  const { toast } = useToast();
  const errToast = buildErrorToast(toast);
  const { data: leaveTypes, isLoading } = useQuery({ queryKey: ['leave-types'], queryFn: getLeaveTypes });
  const perm = useCrudPermissions('S47');

  const createMut = useMutation({
    mutationFn: createLeaveType,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leave-types'] }),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Parameters<typeof updateLeaveType>[1] }) => updateLeaveType(id, dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leave-types'] }),
  });
  const deleteMut = useMutation({
    mutationFn: deleteLeaveType,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leave-types'] }),
  });
  const toggleMut = useMutation({
    mutationFn: toggleLeaveTypeDisabled,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leave-types'] }),
  });

  // Build columns with toggle action (needs toggleMut in scope)
  const columns: ColumnDef<ILeaveType>[] = [
    ...baseColumns,
    {
      accessorKey: 'isDisabled',
      header: 'Enabled',
      size: 13,
      cell: ({ row }) => (
        <Switch
          checked={!row.original.isDisabled}
          onCheckedChange={async () => {
            try {
              await toggleMut.mutateAsync(row.original.id);
            } catch (err: unknown) {
              toast({ title: getApiErrorMessage(err, 'Failed to toggle'), variant: 'destructive' });
            }
          }}
          data-testid={`toggle-${row.original.code}`}
        />
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LeaveTypeFormValues>({
    resolver: zodResolver(leaveTypeSchema) as import('react-hook-form').Resolver<LeaveTypeFormValues>,
    defaultValues: { isPaid: true, requiresAttachment: false },
  });

  function handleAdd() {
    setEditTarget(null);
    reset({ isPaid: true, requiresAttachment: false });
    setDialogOpen(true);
  }

  function handleEdit(row: ILeaveType) {
    setEditTarget(row);
    reset({
      code: row.code,
      name: row.name,
      colorHex: row.colorHex ?? '',
      isPaid: row.isPaid,
      requiresAttachment: row.requiresAttachment,
    });
    setDialogOpen(true);
  }

  async function handleDelete(row: ILeaveType) {
    try {
      await deleteMut.mutateAsync(row.id);
      toast({ title: 'Leave type deleted' });
    } catch (err: unknown) {
      toast({ title: getApiErrorMessage(err, 'Failed to delete leave type'), variant: 'destructive' });
    }
  }

  async function onSubmit(values: LeaveTypeFormValues) {
    try {
      if (editTarget) {
        await updateMut.mutateAsync({
          id: editTarget.id,
          dto: {
            name: values.name,
            colorHex: values.colorHex || undefined,
            isPaid: values.isPaid,
            requiresAttachment: values.requiresAttachment,
            note: values.note,
          },
        });
        toast({ title: 'Leave type updated' });
      } else {
        await createMut.mutateAsync(values);
        toast({ title: 'Leave type created' });
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      errToast(err, editTarget ? 'Failed to update' : 'Failed to create');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" data-testid="page-title-leave-types">Leave Types</h1>
        <p className="text-sm text-muted-foreground">Configure leave type categories</p>
      </div>

      <CrudTable
        columns={columns}
        data={safeArray(leaveTypes)}
        isLoading={isLoading}
        onAdd={perm.canCreate ? handleAdd : undefined}
        onEdit={perm.canUpdate ? handleEdit : undefined}
        onDelete={perm.canDelete ? handleDelete : undefined}
        addLabel="Add Leave Type"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle>
          </DialogHeader>
          <form id="leave-type-form" onSubmit={handleSubmit(onSubmit)} className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <FormTextField
              id="code"
              label="Code"
              required
              registration={register('code')}
              current={watch('code')}
              maxLength={50}
              error={errors.code?.message}
              disabled={!!editTarget}
            />

            <FormTextField
              id="name"
              label="Name"
              required
              registration={register('name')}
              current={watch('name')}
              maxLength={200}
              error={errors.name?.message}
            />

            <div className="space-y-1">
              <Label htmlFor="colorHex">Badge Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="colorHex"
                  type="color"
                  className="w-12 h-9 p-1"
                  {...register('colorHex')}
                />
                <Input
                  className="w-24"
                  value={watch('colorHex') || ''}
                  onChange={(e) => setValue('colorHex', e.target.value)}
                  placeholder="#4CAF50"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="isPaid">Paid Leave</Label>
              <Switch
                id="isPaid"
                checked={watch('isPaid')}
                onCheckedChange={(v) => setValue('isPaid', v)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="requiresAttachment">Requires Attachment (hint)</Label>
              <Switch
                id="requiresAttachment"
                checked={watch('requiresAttachment')}
                onCheckedChange={(v) => setValue('requiresAttachment', v)}
              />
            </div>

            <FormTextField
              id="note"
              label="Note"
              registration={register('note')}
              current={watch('note')}
              error={errors.note?.message}
            />
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="leave-type-form" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
